import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import type { Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/types";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DropIndicator } from "@shared/DropIndicator";
// eslint-disable-next-line no-restricted-imports
import { flexRender, Header as TanstackHeader, HeaderGroup } from "@tanstack/react-table";

import { MinimalTableHeaderCell, MinimalTableRow } from "./MinimalTableComponents";

type Alignment = "left" | "right" | "center";

const getJustifyContent = (align: Alignment) => {
    switch (align) {
        case "right":
            return "flex-end";
        case "center":
            return "center";
        default:
            return "flex-start";
    }
};

const useStyles = makeStyles()((theme) => ({
    pointingCursor: { cursor: "pointer" },
    defaultCursor: { cursor: "default" },
    icon: {
        fontSize: 16,
        color: theme.palette.emteria.link,
    },
    title: {
        fontWeight: 600,
        fontSize: 16,
        lineHeight: 1.2,
        margin: 0,
    },
    sortIconWrap: {
        display: "inline-flex",
        alignItems: "center",
        alignSelf: "center",
        lineHeight: 0,
    },
    content: {
        display: "flex",
        alignItems: "center",
        height: "100%",
        maxHeight: "50px",
        gap: theme.spacing(1),
        padding: theme.spacing(3),
        paddingLeft: 0,
    },
}));

const tint = (color: string, opacity: number) => `linear-gradient(${alpha(color, opacity)}, ${alpha(color, opacity)})`;

const useDragStyles = makeStyles<void, "handle">()((theme, _params, classes) => ({
    cell: {
        position: "relative",
        "&:hover": { backgroundImage: tint(theme.palette.emteria.primary, 0.06) },
        [`&:hover .${classes.handle}`]: { opacity: 1 },
    },
    dragging: { opacity: 0.4 },
    dropTarget: { backgroundImage: tint(theme.palette.emteria.primary, 0.06) },
    dropTargetActive: { backgroundImage: tint(theme.palette.emteria.primary, 0.14) },
    handle: {
        position: "absolute",
        right: theme.spacing(1),
        top: "50%",
        transform: "translateY(-50%)",
        fontSize: 12,
        lineHeight: 1,
        color: theme.palette.emteria.text.secondary,
        cursor: "grab",
        opacity: 0,
        "@media (hover: none)": { opacity: 1 },
    },
}));

export type ColumnDragProps = {
    cellRef: React.RefObject<HTMLTableCellElement | null>;
    handleRef: React.RefObject<HTMLSpanElement | null>;
    onCellPointerEnter: () => void;
    dropEdge: Edge | null;
    isDragging: boolean;
    /**
     * Another column is being dragged and may be dropped here.
     */
    isDropTarget: boolean;
};

const ColumnDragHandle = ({ handleRef }: { handleRef: ColumnDragProps["handleRef"] }) => {
    const { classes } = useDragStyles();
    const { t } = useTranslation(["common"]);
    return (
        <span ref={handleRef} className={classes.handle} title={t("common:table.dragToReorderColumn")}>
            <i className="fa-solid fa-grip-vertical" />
        </span>
    );
};

type HeaderProps<T> = TanstackHeader<T, unknown> & {
    columnDrag?: ColumnDragProps;
};
const Header = <T,>(props: React.PropsWithChildren<HeaderProps<T>>) => {
    const { classes } = useStyles();
    const { classes: dragClasses } = useDragStyles();
    const canSort = props.column.getCanSort();
    const { columnDrag } = props;

    type ColumnMeta = { align?: Alignment };
    const meta = props.column.columnDef.meta as ColumnMeta | undefined;
    const align = meta?.align || "left";

    return (
        <MinimalTableHeaderCell
            ref={columnDrag?.cellRef}
            onPointerEnter={columnDrag?.onCellPointerEnter}
            className={clsx(
                columnDrag && dragClasses.cell,
                columnDrag?.isDragging && dragClasses.dragging,
                columnDrag?.isDropTarget && (columnDrag.dropEdge ? dragClasses.dropTargetActive : dragClasses.dropTarget),
            )}
            title=""
            key={props.id}
            colSpan={props.colSpan}
            align={align}
        >
            {columnDrag ? <ColumnDragHandle handleRef={columnDrag.handleRef} /> : null}
            {columnDrag?.dropEdge ? <DropIndicator edge={columnDrag.dropEdge} /> : null}
            <Box className={classes.content} sx={{ justifyContent: getJustifyContent(align) }}>
                <Typography
                    component="div"
                    className={clsx(classes.title, canSort ? classes.pointingCursor : classes.defaultCursor)}
                    onClick={props.column.getToggleSortingHandler()}
                >
                    {flexRender(props.column.columnDef.header, props.getContext())}
                </Typography>
                <span className={classes.sortIconWrap}>
                    {{
                        asc: <i className={clsx(classes.icon, "fa-solid fa-arrow-up")} />,
                        desc: <i className={clsx(classes.icon, "fa-solid fa-arrow-down")} />,
                    }[props.column.getIsSorted() as string] ?? null}
                </span>
                {props.children}
            </Box>
        </MinimalTableHeaderCell>
    );
};

type TableHeaderProps<T> = TanstackHeader<T, unknown> & {
    sortDirection: string;
    sortCriteria: string;
    onSort: (id: string) => void;
};
const CustomTableHeader = <T,>(props: React.PropsWithChildren<TableHeaderProps<T>>) => {
    const { classes } = useStyles();
    const canSort = props.column.getCanSort();

    type ColumnMeta = { align?: Alignment };
    const meta = props.column.columnDef.meta as ColumnMeta | undefined;
    const align = meta?.align || "left";

    const onSort = () => {
        if (canSort) {
            props.onSort(props.column.id);
        }
    };

    return (
        <MinimalTableHeaderCell title="" key={props.id} colSpan={props.colSpan} align={align}>
            <Box className={classes.content} sx={{ justifyContent: getJustifyContent(align) }}>
                <Typography
                    component="div"
                    className={clsx(classes.title, canSort ? classes.pointingCursor : classes.defaultCursor)}
                    onClick={onSort}
                >
                    {flexRender(props.column.columnDef.header, props.getContext())}
                </Typography>
                <span className={classes.sortIconWrap}>
                    {props.sortDirection === "asc" && props.sortCriteria === props.id ? (
                        <i className={clsx(classes.icon, "fa-solid fa-arrow-up")} />
                    ) : props.sortDirection === "desc" && props.sortCriteria === props.id ? (
                        <i className={clsx(classes.icon, "fa-solid fa-arrow-down")} />
                    ) : null}
                </span>
                {props.children}
            </Box>
        </MinimalTableHeaderCell>
    );
};

type TableHeaderRowsProps<T> = {
    headerGroups: HeaderGroup<T>[];
    renderHeader?: (header: TanstackHeader<T, unknown>) => React.ReactNode;
    trailing?: React.ReactNode;
};

const TableHeaderRows = <T,>({ headerGroups, renderHeader, trailing }: TableHeaderRowsProps<T>) =>
    headerGroups.map((headerGroup) => (
        <MinimalTableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
                <React.Fragment key={header.id}>{renderHeader ? renderHeader(header) : <Header {...header} />}</React.Fragment>
            ))}
            {trailing}
        </MinimalTableRow>
    ));

export { CustomTableHeader, Header, TableHeaderRows };
